import { Component, effect, inject, OnInit, signal } from '@angular/core';
import { firstValueFrom, map } from 'rxjs';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ApiIntakeType, ApiProductRevision, ApiV1Service, ViewService } from 'api';
import { ActivatedRoute, Router } from '@angular/router';
import {
  dirtyErrorStateMatcher,
  FormService,
  requiredAndNotWhitespaceOnly,
} from 'common/form/form.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { MatFormField } from '@angular/material/form-field';
import { MatInput, MatInputModule, MatLabel } from '@angular/material/input';
import { DialogComponent } from 'common/dialog/dialog.component';
import {
  DialogContentDirective,
  DialogFooterDirective,
} from 'common/dialog/dialog-slots.directive';
import { CardButtonsComponent } from 'common/card/buttons/card-buttons.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { toSignal } from '@angular/core/rxjs-interop';
import { ProgressSpinnerComponent } from 'common/progress-spinner/progress-spinner.component';
import { MatOption, MatSelect } from '@angular/material/select';
import { AutofocusDirective } from 'common/form/autofocus.directive';
import { ButtonDirective } from 'common/button/button.directive';

interface AddRevisionDialogData {
  productId?: number;
  latestRevisionIntakeType?: ApiIntakeType | null;
}

@Component({
  templateUrl: 'add-revision.component.html',
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatInputModule,
    DialogComponent,
    DialogContentDirective,
    DialogFooterDirective,
    CardButtonsComponent,
    MatCheckbox,
    ProgressSpinnerComponent,
    MatSelect,
    MatOption,
    AutofocusDirective,
    ButtonDirective,
  ],
})
export class AddRevisionComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly viewService = inject(ViewService);
  readonly formService = inject(FormService);
  private readonly apiService = inject(ApiV1Service);
  private readonly dialogRef = inject(MatDialogRef<AddRevisionComponent>);
  private readonly dialogData = inject<AddRevisionDialogData | null>(MAT_DIALOG_DATA, {
    optional: true,
  });

  private isDialog = false;

  form!: FormGroup;

  supportedIntakeTypes = toSignal(
    this.apiService
      .getIntakeTypes()
      .pipe(
        map(
          (intakeTypes) => new Map(intakeTypes.map((intakeType) => [intakeType.ident, intakeType])),
        ),
      ),
    {
      initialValue: null,
    },
  );

  protected readonly dirtyErrorStateMatcher = dirtyErrorStateMatcher;

  loading = signal(false);
  submitting = signal(false);
  submitted = signal(false);
  protected openRevisionAfterCreation = signal<boolean>(true);

  constructor() {
    effect(() => {
      const supportedIntakeTypes = this.supportedIntakeTypes();
      if (supportedIntakeTypes) {
        const latestSetIntakeType = this.dialogData?.latestRevisionIntakeType;
        if (
          supportedIntakeTypes.size > 0 &&
          latestSetIntakeType &&
          supportedIntakeTypes.has(latestSetIntakeType.ident)
        ) {
          this.form.get('intakeTypeIdent')?.setValue(latestSetIntakeType.ident);
        } else if (supportedIntakeTypes.size > 0) {
          const firstIntakeType = Array.from(supportedIntakeTypes.keys())[0];
          this.form.get('intakeTypeIdent')?.setValue(firstIntakeType);
        }
      }
    });
  }

  async ngOnInit(): Promise<void> {
    this.viewService.triggerRequirementsFetch().subscribe();
    this.form = this.formBuilder.group({
      revision: ['', [requiredAndNotWhitespaceOnly, Validators.maxLength(255)]],
      intakeTypeIdent: ['', [Validators.required]],
    });

    if (this.dialogRef) {
      this.isDialog = true;
    }
  }

  protected onCancel() {
    if (this.isDialog) {
      this.dialogRef.close();
    } else {
      this.router.navigate(['..'], { relativeTo: this.route });
    }
  }

  async onSubmit() {
    this.submitted.set(true);

    if (this.form.invalid) {
      return;
    }

    this.submitting.set(true);
    try {
      const revision = await this.saveProductRevision();
      if (revision.message) {
        // TODO: Maybe show error message in dialog box
        console.log(revision.message);
      }
      if (this.isDialog) {
        this.dialogRef.close({
          revision: revision,
          openRevisionAfterCreation: this.openRevisionAfterCreation(),
        });
      } else {
        await this.router.navigate(['..', revision.id], { relativeTo: this.route });
      }
    } catch (error) {
      console.log(error);
      this.submitting.set(false);
    }
  }

  private async saveProductRevision(): Promise<ApiProductRevision> {
    const productId = this.getProductId();

    return firstValueFrom(this.apiService.createProductRevision(productId, this.form.value));
  }

  private getProductId(): number {
    const productId = this.dialogData?.productId ?? Number(this.route.snapshot.params['productId']);

    if (!Number.isInteger(productId)) {
      throw new Error('Cannot create product revision: missing productId.');
    }

    return productId;
  }

  protected async validateUniqueness() {
    this.formService.runAsyncValidator(this.form, 'revision', ['uniqueness'], () =>
      this.viewService.validateProductRevisionName(
        this.getProductId(),
        this.form.get('revision')?.value || '',
      ),
    );
  }

  protected onToggleOpenRevision() {
    this.openRevisionAfterCreation.set(!this.openRevisionAfterCreation());
  }
}
